import { ArtifactCategoryBadge } from '@/components/artifacts/ArtifactCategoryBadge';
import { ArtifactOverviewMap } from '@/components/artifacts/ArtifactOverviewMap';
import { ArtifactsList } from '@/components/artifacts/ArtifactsList';
import { ArtifactTypeBadge } from '@/components/artifacts/ArtifactTypeBadge';
import type { OwnerScopeProps } from '@/components/shared/ownerScope';
import type { Artifact, ArtifactCategory, ArtifactType } from '@/config/objects/artifacts';
import type { PlacedArtifact } from '@/config/objects/placements';
import { LocationPicker } from '@/components/shared';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormFieldGroup,
  Input,
  Skeleton,
  Switch,
  toast,
  useApiClient,
  useApp,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gem, Lock, MapPin, Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

export interface ArtifactsManagerProps extends OwnerScopeProps {
  /**
   * Copy for the lock banner shown when `structureEditable` is false.
   * The event wrapper passes a status-aware sentence; unused for
   * templates (always editable).
   */
  lockedDescription?: string;
}

/**
 * Owner-scoped manager for placing library artifacts. Serves both events
 * (`apiBase=/events/:id`, `keyPrefix='Event'` — query keys and URLs are
 * byte-identical to the original EventArtifactsTab) and templates
 * (`apiBase=/templates/:id`, `keyPrefix='Template'`).
 */
export function ArtifactsManager({
  ownerId,
  apiBase,
  keyPrefix,
  structureEditable,
  lockedDescription = 'Artifact placement is locked, but you can toggle active state.',
}: ArtifactsManagerProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { config } = useApp();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editArtifact, setEditArtifact] = useState<PlacedArtifact | null>(null);
  const [removeArtifact, setRemoveArtifact] = useState<PlacedArtifact | null>(null);
  // DT-169. The placement dialog now also collects distribution-mode
  // overrides per add. `mode='pre_loaded'` skips the lat/lng
  // pickers entirely and surfaces a quantity field instead.
  const [placementData, setPlacementData] = useState<{
    artifact: Artifact;
    lat: string;
    lng: string;
    mode: 'map_discovery' | 'pre_loaded';
    quantityPerTeam: number;
  } | null>(null);

  const artifactsKey = [`${keyPrefix}Artifacts`, ownerId];
  const ownerNoun = keyPrefix === 'Event' ? 'event' : 'template';

  // Fetch owner artifacts
  const { data: artifactsData, isLoading: artifactsLoading } = useQuery<{
    success: boolean;
    data: PlacedArtifact[];
  }>({
    queryKey: artifactsKey,
    queryFn: () => apiClient.get(`${apiBase}/artifacts`),
    enabled: !!ownerId,
  });

  const artifacts = artifactsData?.data || [];

  // Add artifact mutation
  const addMutation = useMutation({
    mutationFn: async (payload: {
      libraryArtifactId: string;
      latitude?: number;
      longitude?: number;
      distributionMode: 'map_discovery' | 'pre_loaded';
      quantityPerTeam?: number;
    }) => {
      return apiClient.post(`${apiBase}/artifacts`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: artifactsKey });
      toast.success(`Artifact added to ${ownerNoun}`);
      setPlacementData(null);
      setShowAddDialog(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to add artifact');
    },
  });

  // Update artifact mutation
  const updateMutation = useMutation({
    mutationFn: async ({ artifactId, data }: { artifactId: string; data: Record<string, unknown> }) => {
      return apiClient.patch(`${apiBase}/artifacts/${artifactId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: artifactsKey });
      toast.success('Artifact updated');
      setEditArtifact(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to update artifact');
    },
  });

  // Remove artifact mutation
  const removeMutation = useMutation({
    mutationFn: async (artifactId: string) => {
      return apiClient.delete(`${apiBase}/artifacts/${artifactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: artifactsKey });
      toast.success(`Artifact removed from ${ownerNoun}`);
      setRemoveArtifact(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to remove artifact');
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ artifactId, isActive }: { artifactId: string; isActive: boolean }) => {
      return apiClient.patch(`${apiBase}/artifacts/${artifactId}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: artifactsKey });
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to toggle artifact');
    },
  });

  const handleSelectFromLibrary = (artifact: Artifact) => {
    // DT-169. Seed the placement dialog with the library template's
    // default distribution mode + quantity. Admins can override here.
    setPlacementData({
      artifact,
      lat: '',
      lng: '',
      mode: artifact.distributionMode ?? 'map_discovery',
      quantityPerTeam: artifact.quantityPerTeam ?? 3,
    });
    setShowAddDialog(false);
  };

  const handleConfirmPlacement = () => {
    if (!placementData) return;
    if (placementData.mode === 'pre_loaded') {
      // Pre-loaded gadgets have no map location. Quantity is the only
      // mandatory field.
      if (!Number.isFinite(placementData.quantityPerTeam) || placementData.quantityPerTeam < 1) {
        toast.error('Quantity per team must be at least 1');
        return;
      }
      addMutation.mutate({
        libraryArtifactId: placementData.artifact.id,
        distributionMode: 'pre_loaded',
        quantityPerTeam: placementData.quantityPerTeam,
      });
      return;
    }

    const lat = parseFloat(placementData.lat);
    const lng = parseFloat(placementData.lng);
    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Please enter valid coordinates');
      return;
    }
    addMutation.mutate({
      libraryArtifactId: placementData.artifact.id,
      latitude: lat,
      longitude: lng,
      distributionMode: 'map_discovery',
    });
  };

  const handleSaveEdit = () => {
    if (!editArtifact) return;
    // DT-169. Pre-loaded gadgets only carry quantityPerTeam + isActive
    // — no map fields are meaningful. Map artifacts continue to send
    // the existing lat/lng/radius payload.
    if (editArtifact.distributionMode === 'pre_loaded') {
      updateMutation.mutate({
        artifactId: editArtifact.id,
        data: {
          quantityPerTeam: editArtifact.quantityPerTeam ?? 3,
          isActive: editArtifact.isActive,
        },
      });
      return;
    }
    updateMutation.mutate({
      artifactId: editArtifact.id,
      data: {
        latitude: editArtifact.latitude,
        longitude: editArtifact.longitude,
        detectionRadius: editArtifact.detectionRadius,
        claimRadius: editArtifact.claimRadius,
        isActive: editArtifact.isActive,
      },
    });
  };

  if (artifactsLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-9 w-36" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto -m-4 sm:-m-6 p-4 sm:p-6 space-y-4">
      {/* Overview map. DT-169 pre-loaded gadgets have no location and
          would otherwise drop a pin at (0,0); filter them out here. */}
      {(() => {
        const mapArtifacts = artifacts.filter(
          (a) => a.distributionMode !== 'pre_loaded',
        );
        return mapArtifacts.length > 0 ? (
          <ArtifactOverviewMap
            artifacts={mapArtifacts}
            googleMapsApiKey={config?.googleMapsApiKey}
            className="-mx-4 sm:-mx-6 -mt-4 sm:-mt-6 h-72 rounded-b-2xl"
          />
        ) : null;
      })()}

      {/* Lock banner */}
      {!structureEditable && (
        <Alert variant="default" className="bg-amber-50 border-amber-200">
          <Lock className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {lockedDescription}
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gem className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Artifacts</h2>
          <span className="text-sm text-muted-foreground">({artifacts.length})</span>
        </div>
        {structureEditable && (
          <Button onClick={() => setShowAddDialog(true)} size="sm" icon={Plus}>
            Add from Library
          </Button>
        )}
      </div>

      {/* Empty state */}
      {artifacts.length === 0 && (
        <EmptyState
          icon={<Gem className="h-8 w-8" />}
          title="No artifacts placed yet"
          description={
            structureEditable
              ? 'Add artifacts from your library and place them on the map.'
              : `This ${ownerNoun} has no artifacts configured.`
          }
          action={
            structureEditable
              ? { label: 'Add from Library', onClick: () => setShowAddDialog(true), icon: Plus }
              : undefined
          }
        />
      )}

      {/* Artifact cards */}
      {artifacts.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {artifacts.map((artifact) => (
            <Card key={artifact.id} className="gap-0 rounded-xl border bg-card py-0 shadow-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <h3 className="font-medium text-sm truncate">{artifact.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <ArtifactTypeBadge type={artifact.artifactType as ArtifactType} />
                      <ArtifactCategoryBadge category={artifact.artifactCategory as ArtifactCategory} />
                    </div>
                  </div>
                  <Switch
                    checked={artifact.isActive}
                    onCheckedChange={(checked) =>
                      toggleActiveMutation.mutate({ artifactId: artifact.id, isActive: checked })
                    }
                  />
                </div>

                {/* DT-169. Pre-loaded gadgets have no map location;
                    swap the coords + radius row for a quantity badge. */}
                {artifact.distributionMode === 'pre_loaded' ? (
                  <>
                    <div className="flex items-center gap-1.5 text-xs">
                      <Package className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      <span className="font-medium text-emerald-700">Pre-loaded gadget</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {artifact.quantityPerTeam ?? 3} per team at event start
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="font-mono truncate">
                        {artifact.latitude.toFixed(5)}, {artifact.longitude.toFixed(5)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Detection: {artifact.detectionRadius}m | Claim: {artifact.claimRadius}m
                    </div>
                  </>
                )}

                {structureEditable && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setEditArtifact({ ...artifact })}
                    >
                      <Pencil className="h-3 w-3" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => setRemoveArtifact(artifact)}
                    >
                      <Trash2 className="h-3 w-3" />
                      Remove
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add from Library dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="!w-[90vw] !max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Artifact from Library</DialogTitle>
            <DialogDescription>Select an artifact to place in this {ownerNoun}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto -mx-6 px-6">
            <ArtifactsList
              onItemClick={handleSelectFromLibrary}
              className="[&_.crud-list-header]:hidden"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Placement dialog. Two modes:
            - map_discovery: lat/lng pickers (existing flow).
            - pre_loaded: quantityPerTeam input + a note explaining
              the auto-grant on event start. (DT-169) */}
      <Dialog open={!!placementData} onOpenChange={(open) => !open && setPlacementData(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {placementData?.mode === 'pre_loaded' ? 'Configure' : 'Place'}{' '}
              &quot;{placementData?.artifact.name}&quot;
            </DialogTitle>
            <DialogDescription>
              {placementData?.mode === 'pre_loaded'
                ? 'Pre-loaded gadgets are auto-granted to every team when the event starts.'
                : 'Set the location for this artifact on the map'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Distribution-mode toggle */}
            <FormFieldGroup
              name="mode"
              label="Distribution"
              description="How teams receive this artifact"
            >
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { value: 'map_discovery', label: 'On the map', icon: MapPin },
                    { value: 'pre_loaded', label: 'Pre-loaded', icon: Package },
                  ] as const
                ).map(({ value, label, icon: Icon }) => {
                  const isActive = placementData?.mode === value;
                  return (
                    <button
                      type="button"
                      key={value}
                      onClick={() =>
                        placementData && setPlacementData({ ...placementData, mode: value })
                      }
                      className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </FormFieldGroup>

            {placementData?.mode === 'map_discovery' ? (
              <>
                <LocationPicker
                  latitude={placementData?.lat ?? ''}
                  longitude={placementData?.lng ?? ''}
                  onLocationChange={(lat, lng) => {
                    if (placementData) {
                      setPlacementData({ ...placementData, lat, lng });
                    }
                  }}
                  googleMapsApiKey={config?.googleMapsApiKey}
                />
                <div className="grid gap-4 grid-cols-2">
                  <FormFieldGroup name="lat" label="Latitude">
                    <Input
                      type="number"
                      step="any"
                      value={placementData?.lat ?? ''}
                      onChange={(e) =>
                        placementData && setPlacementData({ ...placementData, lat: e.target.value })
                      }
                      placeholder="e.g., 40.7128"
                    />
                  </FormFieldGroup>
                  <FormFieldGroup name="lng" label="Longitude">
                    <Input
                      type="number"
                      step="any"
                      value={placementData?.lng ?? ''}
                      onChange={(e) =>
                        placementData && setPlacementData({ ...placementData, lng: e.target.value })
                      }
                      placeholder="e.g., -74.0060"
                    />
                  </FormFieldGroup>
                </div>
              </>
            ) : (
              <FormFieldGroup
                name="quantityPerTeam"
                label="Quantity per team"
                description="How many copies each team starts the event with"
              >
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={placementData?.quantityPerTeam ?? 3}
                  onChange={(e) =>
                    placementData &&
                    setPlacementData({
                      ...placementData,
                      quantityPerTeam: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </FormFieldGroup>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setPlacementData(null)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmPlacement} loading={addMutation.isPending}>
                {placementData?.mode === 'pre_loaded' ? 'Add Gadget' : 'Place Artifact'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog. Splits into two layouts based on the artifact's
          distributionMode: pre-loaded gadgets only show quantity +
          active toggle; map-discovery shows the existing lat/lng +
          radius UI. */}
      <Dialog open={!!editArtifact} onOpenChange={(open) => !open && setEditArtifact(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit &quot;{editArtifact?.name}&quot;</DialogTitle>
            <DialogDescription>
              {editArtifact?.distributionMode === 'pre_loaded'
                ? 'Pre-loaded gadget settings'
                : 'Update placement and settings'}
            </DialogDescription>
          </DialogHeader>
          {editArtifact && editArtifact.distributionMode === 'pre_loaded' && (
            <div className="space-y-4">
              <FormFieldGroup
                name="editQuantity"
                label="Quantity per team"
                description="Updating this won't retroactively change teams that already received their grants."
              >
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={editArtifact.quantityPerTeam ?? 3}
                  onChange={(e) =>
                    setEditArtifact({
                      ...editArtifact,
                      quantityPerTeam: parseInt(e.target.value) || 1,
                    })
                  }
                />
              </FormFieldGroup>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editArtifact.isActive}
                  onCheckedChange={(checked) =>
                    setEditArtifact({ ...editArtifact, isActive: checked })
                  }
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setEditArtifact(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} loading={updateMutation.isPending}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
          {editArtifact && editArtifact.distributionMode !== 'pre_loaded' && (
            <div className="space-y-4">
              <LocationPicker
                latitude={editArtifact.latitude.toString()}
                longitude={editArtifact.longitude.toString()}
                onLocationChange={(lat, lng) => {
                  setEditArtifact({
                    ...editArtifact,
                    latitude: parseFloat(lat) || editArtifact.latitude,
                    longitude: parseFloat(lng) || editArtifact.longitude,
                  });
                }}
                googleMapsApiKey={config?.googleMapsApiKey}
              />
              <div className="grid gap-4 grid-cols-2">
                <FormFieldGroup name="editLat" label="Latitude">
                  <Input
                    type="number"
                    step="any"
                    value={editArtifact.latitude}
                    onChange={(e) =>
                      setEditArtifact({ ...editArtifact, latitude: parseFloat(e.target.value) || 0 })
                    }
                  />
                </FormFieldGroup>
                <FormFieldGroup name="editLng" label="Longitude">
                  <Input
                    type="number"
                    step="any"
                    value={editArtifact.longitude}
                    onChange={(e) =>
                      setEditArtifact({ ...editArtifact, longitude: parseFloat(e.target.value) || 0 })
                    }
                  />
                </FormFieldGroup>
              </div>
              <div className="grid gap-4 grid-cols-2">
                <FormFieldGroup name="editDetection" label="Detection Radius (m)">
                  <Input
                    type="number"
                    value={editArtifact.detectionRadius}
                    onChange={(e) =>
                      setEditArtifact({ ...editArtifact, detectionRadius: parseInt(e.target.value) || 100 })
                    }
                  />
                </FormFieldGroup>
                <FormFieldGroup name="editClaim" label="Claim Radius (m)">
                  <Input
                    type="number"
                    value={editArtifact.claimRadius}
                    onChange={(e) =>
                      setEditArtifact({ ...editArtifact, claimRadius: parseInt(e.target.value) || 10 })
                    }
                  />
                </FormFieldGroup>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editArtifact.isActive}
                  onCheckedChange={(checked) =>
                    setEditArtifact({ ...editArtifact, isActive: checked })
                  }
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setEditArtifact(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} loading={updateMutation.isPending}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <ConfirmDialog
        open={!!removeArtifact}
        onOpenChange={(open) => !open && setRemoveArtifact(null)}
        title="Remove Artifact"
        description={`Are you sure you want to remove "${removeArtifact?.name}" from this ${ownerNoun}?`}
        confirmText="Remove"
        variant="destructive"
        loading={removeMutation.isPending}
        onConfirm={() => { if (removeArtifact) removeMutation.mutate(removeArtifact.id); }}
      />
    </div>
  );
}
